import React from 'react';
import { cn } from '@crisellevaldez/speculo-ui';
import {
  Sparkles,
  User,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import type { Contact } from '../../../../types/contact.types';
import type { GQLAiPreferences } from '../../../../hooks/graphql/contacts/useContact';
import { IconPill } from '../../../common/IconPill';

interface ContactSidePanelProps {
  contact: Contact | null;
}

// Reads contact.aiIntelligence (SPEC-1709); each card empty-states when its slice is absent.
export const ContactSidePanel: React.FC<ContactSidePanelProps> = ({ contact }) => {
  if (!contact) return null;

  const firstName = contact.firstName?.trim() || 'this contact';
  const ai = contact.aiIntelligence ?? null;

  const whatRemiKnows = ai?.whatRemiKnows ?? [];
  const objections = ai?.objections ?? [];
  const commitments = ai?.openCommitments ?? [];
  const nextTouch = ai?.nextScheduledTouch ?? null;
  const prefLines = formatPreferences(ai?.preferences ?? null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
      {/* What Remi knows — running mental model */}
      <SideCard
        icon={Sparkles}
        title="What Remi knows"
        accent="amber"
      >
        {whatRemiKnows.length > 0 ? (
          <ul className="space-y-1 text-xs xl:text-sm">
            {whatRemiKnows.map((line, i) => (
              <li key={i} className="flex items-start gap-1.5 text-foreground/85">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-500/70 flex-shrink-0" />
                <span className="leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs xl:text-sm text-muted-foreground italic leading-relaxed">
            Remi hasn't built up a picture of {firstName} yet.
          </p>
        )}
      </SideCard>

      {/* Preferences — learned */}
      <SideCard icon={User} title="Preferences">
        {prefLines.length > 0 ? (
          <ul className="space-y-1 text-xs xl:text-sm">
            {prefLines.map((line, i) => (
              <li key={i} className="flex items-start gap-1.5 text-foreground/80">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-foreground/30 flex-shrink-0" />
                <span className="leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs xl:text-sm text-muted-foreground italic">
            No preferences learned yet.
          </p>
        )}
      </SideCard>

      {/* Objections — raised */}
      <SideCard icon={AlertTriangle} title="Objections">
        {objections.length > 0 ? (
          <ul className="space-y-1 text-xs xl:text-sm">
            {objections.map((o, i) => (
              <li key={i} className="flex items-start gap-1.5 text-foreground/80">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-foreground/30 flex-shrink-0" />
                <span className="leading-relaxed">{o.description || o.insightKey}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs xl:text-sm text-muted-foreground italic">
            No objections recorded yet.
          </p>
        )}
      </SideCard>

      {/* Open commitments — promises */}
      <SideCard icon={CheckCircle2} title="Open commitments">
        {commitments.length > 0 ? (
          <ul className="space-y-1 text-xs xl:text-sm">
            {commitments.map((c, i) => (
              <li key={i} className="flex items-start gap-1.5 text-foreground/80">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-foreground/30 flex-shrink-0" />
                <span className="leading-relaxed">{c.description || c.insightKey}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs xl:text-sm text-muted-foreground italic">
            No open commitments right now.
          </p>
        )}
      </SideCard>

      {/* Next scheduled touch */}
      <SideCard icon={Clock} title="Next scheduled touch">
        {nextTouch && (nextTouch.channel || nextTouch.scheduledAt || nextTouch.reason) ? (
          <div className="text-xs xl:text-sm text-foreground/85 space-y-0.5">
            <p className="font-medium">
              {[formatChannel(nextTouch.channel), formatWhen(nextTouch.scheduledAt)]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {nextTouch.reason && (
              <p className="text-muted-foreground leading-relaxed">{nextTouch.reason}</p>
            )}
          </div>
        ) : (
          <p className="text-xs xl:text-sm text-muted-foreground italic">
            No upcoming touch scheduled.
          </p>
        )}
      </SideCard>
    </div>
  );
};

/* ── Preferences → readable lines ──────────────────────────────── */
function formatPreferences(p: GQLAiPreferences | null): string[] {
  if (!p) return [];
  const lines: string[] = [];

  const fmtMoney = (n: number) =>
    n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`;
  if (p.budgetMin != null || p.budgetMax != null) {
    if (p.budgetMin != null && p.budgetMax != null)
      lines.push(`Budget ${fmtMoney(p.budgetMin)}–${fmtMoney(p.budgetMax)}`);
    else if (p.budgetMax != null) lines.push(`Budget up to ${fmtMoney(p.budgetMax)}`);
    else if (p.budgetMin != null) lines.push(`Budget from ${fmtMoney(p.budgetMin)}`);
  }
  if (p.propertyTypes?.length) lines.push(`Property: ${p.propertyTypes.join(', ')}`);
  if (p.locationsOfInterest?.length)
    lines.push(`Locations: ${p.locationsOfInterest.join(', ')}`);
  if (p.bedroomsMin != null || p.bedroomsMax != null) {
    if (p.bedroomsMin != null && p.bedroomsMax != null)
      lines.push(`${p.bedroomsMin}–${p.bedroomsMax} beds`);
    else lines.push(`${p.bedroomsMin ?? p.bedroomsMax}+ beds`);
  }
  if (p.bathroomsMin != null) lines.push(`${p.bathroomsMin}+ baths`);
  if (p.sqftMin != null || p.sqftMax != null) {
    if (p.sqftMin != null && p.sqftMax != null)
      lines.push(`${p.sqftMin}–${p.sqftMax} sqft`);
    else lines.push(`${p.sqftMin ?? p.sqftMax}+ sqft`);
  }
  if (p.mustHaveFeatures?.length)
    lines.push(`Must-have: ${p.mustHaveFeatures.join(', ')}`);
  if (p.niceToHaveFeatures?.length)
    lines.push(`Nice-to-have: ${p.niceToHaveFeatures.join(', ')}`);
  if (p.timeline) lines.push(`Timeline: ${p.timeline}`);
  if (p.intentLevel) lines.push(`Intent: ${p.intentLevel}`);

  return lines;
}

function formatChannel(channel: string | null): string {
  if (!channel) return '';
  const c = channel.toLowerCase();
  if (c === 'sms') return 'SMS';
  if (c === 'call') return 'Call';
  if (c === 'email') return 'Email';
  return channel.charAt(0).toUpperCase() + channel.slice(1);
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/* ── Card primitive ────────────────────────────────────────────── */
const ACCENT_CLASSES: Record<'amber' | 'default', string> = {
  amber: 'bg-amber-50 dark:bg-amber-500/10 ring-amber-200 dark:ring-amber-500/30',
  default: 'bg-white dark:bg-card ring-foreground/10',
};

const SideCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
  accent?: 'amber' | 'default';
  /** Right-aligned action slot in the card header (e.g. an AskRemi button). */
  action?: React.ReactNode;
}> = ({ icon, title, children, accent = 'default', action }) => (
  <div className={cn('relative rounded-xl ring-1 shadow-md p-3.5', ACCENT_CLASSES[accent])}>
    <div className="flex items-center gap-1.5 mb-2">
      <IconPill icon={icon} size="md" tone={accent === 'amber' ? 'amber' : 'muted'} />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground/70 flex-1 min-w-0">
        {title}
      </span>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
    {children}
  </div>
);
